import { usePostHog } from 'posthog-js/react';
import type { PostHog } from 'posthog-js';
import JwtValidator from '../common/JwtValidator';
import { Jwt, JwtRole } from '@thingslog/repositories';

interface PostHogEventEmitter {
  syncIdentity: (jwt: Jwt | null) => void;
  capturePageview: (url: string) => void;
}

export const usePostHogEventEmitter = (): PostHogEventEmitter => {
  const postHog = usePostHog();
  const jwtValidator = new JwtValidator();
  const decodedToken = jwtValidator.decodedToken;

  const syncIdentity = (jwt: Jwt | null): void => {
    if (!postHog) return;

    if (jwt) {
      postHog.identify(jwt.email, {
        email: jwt.email,
        companyId: jwt.companyId,
        companyName: jwt.companyName,
        solution: jwt.solution,
        roles: jwt.roles?.map((r: JwtRole) => r.authority) ?? [],
        language: jwt.language,
        dashboardEnabled: jwt.isDashboardEnabled,
      });

      if (jwt.companyId) {
        postHog.group('company', jwt.companyId, { id: jwt.companyId, name: jwt.companyName });
      }
      if (jwt.solution) {
        postHog.group('solution', jwt.solution);
      }
    } else {
      postHog.reset();
    }
  };

  const capturePageview = (url: string): void => {
    if (!postHog) return;

    setTimeout(() => {
      postHog.capture('$pageview', { path: url });
    }, 1);
  };

  return {
    syncIdentity,
    capturePageview,
  };
};
